import { useMemo, useState } from "react";
import {
  getFitContainerRotationScale,
  getFitRotationScale,
} from "src/libs/geometry-lib";

/**
 * Take an image that is assumed to already be scaled to fit into a container
 * (`ref`), rotates it, and then returns a scale factor that can be used to
 * scale the image to the size of the container after rotation.
 */
export function useRotationScaleToFit(
  /** Will return a scale factor that can be used to scale the image to the size
   *of this element. */
  ref: React.RefObject<HTMLElement | null>,
  image: { width: number; height: number },
  rotateDegrees: number,
  options: {
    // if true, will use the size of the parent instead of the element itself
    useParent?: boolean;
  } = { useParent: false }
): number {
  const [numTries, setNumTries] = useState(0);

  return useMemo(() => {
    if (image.width === 0 || image.height === 0) {
      return 1;
    }

    // This is a pretty good fallback. It doesn't take the size of the parent
    // into account, but it's better than nothing.
    const fallbackScale = getFitRotationScale({
      width: image.width,
      height: image.height,
      rotateDegrees,
    });

    // If ref isn't set, it probably hasn't been mounted yet. If we async for
    // 1ms it should mount before the next render. If not, then assume it isn't
    // going to mount.
    if (!ref.current) {
      if (numTries === 0) {
        setTimeout(() => {
          setNumTries((x) => x + 1);
        }, 1);
      }

      return fallbackScale;
    }

    const el = ref.current;

    // we want to scale to fit the parent
    const elSize = options.useParent
      ? el.parentElement?.getBoundingClientRect()
      : el.getBoundingClientRect();

    if (!elSize) {
      return fallbackScale;
    }

    const scale = getFitContainerRotationScale({
      image: { width: image.width, height: image.height },
      container: elSize,
      rotateDegrees,
    });

    return scale;
  }, [
    ref,
    image.width,
    image.height,
    rotateDegrees,
    numTries,
    options.useParent,
  ]);
}
